import React, { useEffect, useMemo, useState } from 'react';
import { Card, DataTable } from '../../../components/ui';
import { DynamicColumn, DynamicListTable } from '../../../components/DynamicListTable';
import { NumerosGrid, TipoNumerosGrid } from '../../../components/NumerosGrid';
import { fetchAPI } from '../../../services/apiClient';
import { fechaLocalYMD } from '../../../utils/date';
import { Printer, QrCode } from 'lucide-react';
import { isNativePrintSelected, printRawByDestination } from '../../../utils/printer';
import { useAuth } from '../../../app/auth';
import { generarPdfListaBlob } from '../../../utils/pdfListas';
import { shareImageNative } from '../../../utils/nativeShare';
import { openWhatsApp } from '../../../utils/whatsappShare';
import {
  FilaFusionInformativa,
  TipoListaFusionInformativa,
  fusionarListasInformativas,
} from '../utils/fusionListasInformativas';

interface SorteoItem {
  id: string;
  tipo_id: string;
  nombre: string;
  fecha: string;
  estado: string;
  jugadas_hoy?: number;
}

interface LocalItem {
  id: string;
  nombre: string;
}

interface PaqueteResumen {
  paquete_id: string;
  paquete_nombre: string;
  local_ids: string[];
}

interface ResumenLocalRow {
  local_id: string;
  local_nombre: string;
  total_jugadas: number;
  total_vendedores: number;
  total_monto: number;
}

interface ListaQRItem {
  numero: string;
  tipo_juego: string | null;
  bloque_hex: string;
  bloque_hex_interpretado: string;
  monto: number;
  posicion: number;
  normalizado: boolean;
}

interface ListaQRPreview {
  items: ListaQRItem[];
  total_calculado: string;
  total_reportado: string | null;
  total_valido: boolean | null;
  cantidad_bloques: number;
  modo: string;
  posicion_inicio: number;
  estructura_detectada: string;
  longitud_normalizada: number;
  remanente_hex: string | null;
  metadata_detectada: { fecha_iso?: string; fecha_yyyymmdd?: string; remanente_hex?: string } | null;
  normalizacion_aplicada: boolean;
  errores: string[];
}

const monto = (value: unknown): string => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '0';
  return Math.trunc(n).toLocaleString('es-CR');
};

const mapMontos = (rows: any[], keys: string[]): any[] =>
  rows.map((row, index) => {
    const out = { ...row, id: row.id ?? index + 1 };
    for (const key of keys) {
      if (key in out) out[key] = monto(out[key]);
    }
    return out;
  });



const columnsByTipo = (tipo: string): DynamicColumn[] => {
  switch (tipo) {
    case 'NORMAL':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '80px' },
        { key: 'monto_vendedor', label: 'VENDEDOR', type: 'readonly', width: '140px' },
        { key: 'monto_local', label: 'LOCAL', type: 'readonly', width: '140px' },
        { key: 'monto_banca', label: 'BANCA', type: 'readonly', width: '140px' },
        { key: 'monto_total', label: 'TOTAL', type: 'readonly', width: '140px' },
      ];
    case 'REVENTADO':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '120px' },
        { key: 'vendido_rev1', label: 'REV1', type: 'readonly', width: '160px' },
        { key: 'vendido_rev2', label: 'REV2', type: 'readonly', width: '160px' },
      ];
    case 'MEGA':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '100px' },
        { key: 'rev1', label: 'REV1', type: 'readonly', width: '130px' },
        { key: 'rev2', label: 'REV2', type: 'readonly', width: '130px' },
        { key: 'num2', label: 'NÚM2', type: 'readonly', width: '80px' },
        { key: 'monto_mega', label: 'MONTO', type: 'readonly', width: '140px' },
      ];
    case 'MONAZOS':
      return [
        { key: 'numero', label: 'NUM', type: 'readonly', width: '120px' },
        { key: 'tipo', label: 'TIPO', type: 'readonly', width: '120px' },
        { key: 'vendido', label: 'MONTO', type: 'readonly', width: '180px' },
      ];
    case 'FECHAS':
      return [
        { key: 'numero', label: 'FECHA', type: 'readonly', width: '100px' },
        { key: 'monto_vendedor', label: 'VENDEDOR', type: 'readonly', width: '140px' },
        { key: 'monto_local', label: 'LOCAL', type: 'readonly', width: '140px' },
        { key: 'monto_banca', label: 'BANCA', type: 'readonly', width: '140px' },
        { key: 'monto_total', label: 'TOTAL', type: 'readonly', width: '140px' },
      ];
    case 'PARLEY':
      return [
        { key: 'num1', label: 'NUM1', type: 'readonly', width: '120px' },
        { key: 'num2', label: 'NUM2', type: 'readonly', width: '120px' },
        { key: 'vendido', label: 'MONTO', type: 'readonly', width: '180px' },
      ];
    default:
      return [];
  }
};

const montoKeysByTipo: Record<string, string[]> = {
  NORMAL: ['monto_vendedor', 'monto_local', 'monto_banca', 'monto_total'],
  REVENTADO: ['vendido_rev1', 'vendido_rev2'],
  MEGA: ['rev1', 'rev2', 'monto_mega'],
  MONAZOS: ['vendido'],
  FECHAS: ['monto_vendedor', 'monto_local', 'monto_banca', 'monto_total'],
  PARLEY: ['vendido'],
};

export const ListasPage: React.FC = () => {
  const { user } = useAuth();
  const esBanca = user?.role === 'BANCA';
  const [sorteos, setSorteos] = useState<SorteoItem[]>([]);
  const [locales, setLocales] = useState<LocalItem[]>([]);
  const [selectedSorteoId, setSelectedSorteoId] = useState('');
  const [selectedLocalId, setSelectedLocalId] = useState('');
  const [fechaFiltro, setFechaFiltro] = useState(fechaLocalYMD);
  const [rows, setRows] = useState<any[]>([]);
  const [refreshListasTick, setRefreshListasTick] = useState(0);
  const [resumenLocales, setResumenLocales] = useState<ResumenLocalRow[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [paquetes, setPaquetes] = useState<PaqueteResumen[]>([]);
  const [selectedPaqueteId, setSelectedPaqueteId] = useState('');
  const [enviandoWa, setEnviandoWa] = useState(false);
  const [activeTab, setActiveTab] = useState<'LISTAS' | 'QR'>('LISTAS');
  const [qrOrigen, setQrOrigen] = useState<'QR' | 'MANUAL'>('QR');
  const [qrPayload, setQrPayload] = useState('');
  const [qrModo, setQrModo] = useState<'NORMAL_00_99' | 'DIRECTO_REVENTADO'>('NORMAL_00_99');
  const [qrPosicionInicio, setQrPosicionInicio] = useState('0');
  const [qrCantidadBloques, setQrCantidadBloques] = useState('');
  const [qrTotalEsperado, setQrTotalEsperado] = useState('');
  const [qrPreview, setQrPreview] = useState<ListaQRPreview | null>(null);
  const [qrEditableItems, setQrEditableItems] = useState<ListaQRItem[]>([]);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrSaving, setQrSaving] = useState(false);
  const [qrMensaje, setQrMensaje] = useState('');
  const [fusionActiva, setFusionActiva] = useState(false);
  const [fusionSorteoAId, setFusionSorteoAId] = useState('');
  const [fusionSorteoBId, setFusionSorteoBId] = useState('');
  const [fusionFilas, setFusionFilas] = useState<FilaFusionInformativa[]>([]);
  const [fusionLoading, setFusionLoading] = useState(false);
  const [fusionMensaje, setFusionMensaje] = useState('');

  // Workflow: listas de locales pendientes de oficializar
  interface ListaLocalPendiente {
    id: string;
    local_id: string;
    local_nombre: string;
    estado: string;
    enviada_banca_at: string | null;
    jugadas_count: number;
  }
  const [pendientes, setPendientes] = useState<ListaLocalPendiente[]>([]);
  const [loadingOficializar, setLoadingOficializar] = useState(false);
  const [msgOficializar, setMsgOficializar] = useState('');

  const selectedSorteo = useMemo(
    () => sorteos.find((s) => s.id === selectedSorteoId) || null,
    [sorteos, selectedSorteoId],
  );

  const sorteosFusionables = useMemo(
    () => sorteos.filter((s) => ['NORMAL', 'REVENTADO'].includes((s.tipo_id || '').toUpperCase())),
    [sorteos],
  );

  const sorteoFusionA = useMemo(
    () => sorteosFusionables.find((s) => s.id === fusionSorteoAId) || null,
    [sorteosFusionables, fusionSorteoAId],
  );

  const sorteoFusionB = useMemo(
    () => sorteosFusionables.find((s) => s.id === fusionSorteoBId) || null,
    [sorteosFusionables, fusionSorteoBId],
  );

  const tipoFusion = sorteoFusionA && sorteoFusionB
    ? `${sorteoFusionA.tipo_id} + ${sorteoFusionB.tipo_id}`
    : '';

  const fusionGridRows = useMemo(
    () => fusionFilas.map((row) => ({
      numero: row.numero,
      vendido_rev1: row.monto_r1,
      vendido_rev2: row.monto_r2,
    })),
    [fusionFilas],
  );

  const fusionTotalTemporal = useMemo(
    () => fusionFilas.reduce((sum, row) => sum + row.monto_total, 0),
    [fusionFilas],
  );

  useEffect(() => {
    Promise.all([
      fetchAPI<SorteoItem[]>(`/sorteos?fecha=${fechaFiltro}`),
      fetchAPI<LocalItem[]>('/listas/locales'),
      fetchAPI<PaqueteResumen[]>('/paquetes-reglas/resumen-banquero'),
    ])
      .then(([sorteosData, localesData, paquetesData]) => {
        setSorteos(sorteosData);
        setLocales(localesData);
        setPaquetes(paquetesData || []);
        setSelectedSorteoId((prev) => {
          if (!sorteosData.length) return '';
          const match = sorteosData.find((s) => s.id === prev);
          if (match) return prev;
          // Preferir sorteo con más jugadas hoy; si ninguno tiene, usar el primero
          const conData = [...sorteosData].sort((a, b) => (b.jugadas_hoy ?? 0) - (a.jugadas_hoy ?? 0));
          return (conData[0]?.jugadas_hoy ?? 0) > 0 ? conData[0].id : sorteosData[0].id;
        });
      })
      .catch((err) => console.error('Error cargando listas banca:', err));
  }, [fechaFiltro]);

  useEffect(() => {
    if (!selectedSorteoId) return;
    setLoadingRows(true);
    const tipo = selectedSorteo?.tipo_id || '';
    const params = new URLSearchParams();
    if (selectedLocalId) params.append('local_id', selectedLocalId);
    params.append('fecha', fechaFiltro);

    let listaPath: string;
    if (selectedPaqueteId) {
      params.append('paquete_id', selectedPaqueteId);
      listaPath = `/listas/consolidada-paquete/${selectedSorteoId}?${params.toString()}`;
    } else {
      const suffix = `?${params.toString()}`;
      listaPath = tipo === 'MEGA'
        ? `/listas/individuales/${selectedSorteoId}${suffix}`
        : `/listas/consolidada/${selectedSorteoId}${suffix}`;
    }

    fetchAPI<any[]>(listaPath)
      .then((data) => {
        if (tipo === 'NORMAL') {
          setRows((data || []).map((r: any) => ({
            numero: String(r.numero).padStart(2, '0'),
            vendido: Number(r.monto_total) || 0,
          })));
        } else {
          setRows(data || []);
        }
      })
      .catch((err) => {
        console.error('Error cargando consolidado:', err);
        setRows([]);
      })
      .finally(() => setLoadingRows(false));
  }, [selectedSorteoId, selectedLocalId, selectedPaqueteId, selectedSorteo?.tipo_id, fechaFiltro, refreshListasTick]);

  useEffect(() => {
    if (!selectedSorteoId) return;
    setLoadingResumen(true);
    fetchAPI<ResumenLocalRow[]>(`/listas/resumen-locales/${selectedSorteoId}?fecha=${fechaFiltro}`)
      .then((data) => setResumenLocales(data || []))
      .catch((err) => {
        console.error('Error cargando resumen locales:', err);
        setResumenLocales([]);
      })
      .finally(() => setLoadingResumen(false));
  }, [selectedSorteoId, fechaFiltro, refreshListasTick]);

  useEffect(() => {
    setFusionFilas([]);
    setFusionMensaje('');
  }, [fechaFiltro, selectedLocalId, selectedPaqueteId]);

  // Filas formateadas para DynamicListTable (tipos no NORMAL/REVENTADO/MEGA)
  const formattedRows = useMemo(() => {
    const tipo = selectedSorteo?.tipo_id || '';
    if (tipo === 'REVENTADO') return rows;
    return mapMontos(rows, montoKeysByTipo[tipo] || ['vendido']);
  }, [rows, selectedSorteo?.tipo_id]);

  // Totales consolidados para NORMAL y FECHAS (5 columnas)
  const totalesConsolidado = useMemo(() => {
    const tipo = selectedSorteo?.tipo_id || '';
    if (!['NORMAL', 'FECHAS'].includes(tipo) || rows.length === 0) return null;
    return {
      monto_vendedor: rows.reduce((s, r) => s + (r.monto_vendedor || 0), 0),
      monto_local: rows.reduce((s, r) => s + (r.monto_local || 0), 0),
      monto_banca: rows.reduce((s, r) => s + (r.monto_banca || 0), 0),
      monto_total: rows.reduce((s, r) => s + (r.monto_total || 0), 0),
    };
  }, [rows, selectedSorteo?.tipo_id]);

  const totalJugadas = resumenLocales.reduce((sum, row) => sum + (row.total_jugadas || 0), 0);
  const totalVendedores = resumenLocales.reduce((sum, row) => sum + (row.total_vendedores || 0), 0);
  const totalMonto = resumenLocales.reduce((sum, row) => sum + (row.total_monto || 0), 0);

  // Cargar listas pendientes de oficializar cuando cambia el sorteo
  const cargarPendientes = (sorteoId: string) => {
    fetchAPI<ListaLocalPendiente[]>(`/listas/banca/pendientes?sorteo_id=${sorteoId}`)
      .then(data => setPendientes(data || []))
      .catch(() => setPendientes([]));
  };

  useEffect(() => {
    if (!selectedSorteoId) { setPendientes([]); return; }
    cargarPendientes(selectedSorteoId);
  }, [selectedSorteoId, fechaFiltro]);

  const handleOficializar = async () => {
    if (!pendientes.length || loadingOficializar) return;
    setLoadingOficializar(true);
    setMsgOficializar('');
    try {
      await fetchAPI('/listas/banca/oficializar', {
        method: 'POST',
        body: JSON.stringify({ lista_local_id: pendientes[0].id }),
      });
      setMsgOficializar(`${pendientes.length} lista(s) oficializada(s) correctamente.`);
      setPendientes([]);
    } catch (err: any) {
      setMsgOficializar(err?.message || 'Error al oficializar');
    } finally {
      setLoadingOficializar(false);
    }
  };

  const handleEnviarWa = async () => {
    if (!selectedSorteo || rows.length === 0 || enviandoWa) return;
    setEnviandoWa(true);
    try {
      const paq = paquetes.find((p) => p.paquete_id === selectedPaqueteId);
      const blob = await generarPdfListaBlob({
        sorteo: selectedSorteo,
        fecha: fechaFiltro,
        paqueteNombre: paq ? paq.paquete_nombre : null,
        localNombre: selectedLocalId
          ? locales.find((l) => l.id === selectedLocalId)?.nombre || null
          : null,
        tipo: selectedSorteo.tipo_id || 'NORMAL',
        rows,
      });
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const safeName = selectedSorteo.nombre.replace(/[^a-zA-Z0-9_-]/g, '_');
      const fileName = `lista-${safeName}-${fechaFiltro}-${ts}.pdf`;
      const paqNombre = paq ? paq.paquete_nombre : 'General';
      const texto = `Lista del sorteo ${selectedSorteo.nombre} - ${fechaFiltro} - Paquete: ${paqNombre}`;
      const shared = await shareImageNative(blob, fileName, texto);
      if (!shared) {
        openWhatsApp(texto);
      }
    } catch (err) {
      console.error('Error enviando por WhatsApp:', err);
    } finally {
      setEnviandoWa(false);
    }
  };

  const cargarListaParaFusion = async (sorteo: SorteoItem) => {
    const tipo = (sorteo.tipo_id || '').toUpperCase() as TipoListaFusionInformativa;
    const params = new URLSearchParams();
    if (selectedLocalId) params.append('local_id', selectedLocalId);
    params.append('fecha', fechaFiltro);

    let listaPath: string;
    if (selectedPaqueteId) {
      params.append('paquete_id', selectedPaqueteId);
      listaPath = `/listas/consolidada-paquete/${sorteo.id}?${params.toString()}`;
    } else {
      listaPath = `/listas/consolidada/${sorteo.id}?${params.toString()}`;
    }

    const data = await fetchAPI<any[]>(listaPath);
    return { tipo, rows: data || [] };
  };

  const handleFusionarListas = async () => {
    if (!sorteoFusionA || !sorteoFusionB || fusionLoading) return;
    if (sorteoFusionA.id === sorteoFusionB.id) {
      setFusionMensaje('Seleccione dos listas diferentes para fusionar.');
      setFusionFilas([]);
      return;
    }

    setFusionLoading(true);
    setFusionMensaje('');
    try {
      const [listaA, listaB] = await Promise.all([
        cargarListaParaFusion(sorteoFusionA),
        cargarListaParaFusion(sorteoFusionB),
      ]);
      const filas = fusionarListasInformativas(listaA, listaB);
      setFusionFilas(filas);
      setFusionMensaje(filas.length ? '' : 'Las listas seleccionadas no tienen montos para mostrar.');
    } catch (err: any) {
      console.error('Error fusionando listas informativas:', err);
      setFusionFilas([]);
      setFusionMensaje(err?.message || 'Error cargando la vista fusionada.');
    } finally {
      setFusionLoading(false);
    }
  };

  const procesarQR = async () => {
    if (!qrPayload.trim() || qrLoading) return;
    setQrLoading(true);
    setQrMensaje('');
    setQrPreview(null);
    setQrEditableItems([]);
    try {
      if (qrOrigen === 'MANUAL') {
        const items = qrPayload.split(/\r?\n/)
          .map((line, index) => {
            const [numeroRaw, montoRaw, tipoRaw] = line.split(/[;,\s]+/).filter(Boolean);
            return {
              numero: String(numeroRaw || '').padStart(2, '0'),
              tipo_juego: tipoRaw ? tipoRaw.toUpperCase() : null,
              bloque_hex: '',
              bloque_hex_interpretado: '',
              monto: Number(montoRaw) || 0,
              posicion: index,
              normalizado: false,
            };
          })
          .filter((item) => item.numero && item.monto > 0);
        const total = items.reduce((sum, item) => sum + item.monto, 0);
        setQrPreview({
          items,
          total_calculado: String(total),
          total_reportado: null,
          total_valido: null,
          cantidad_bloques: items.length,
          modo: qrModo,
          posicion_inicio: 0,
          estructura_detectada: 'CARGA_MANUAL',
          longitud_normalizada: qrPayload.length,
          remanente_hex: null,
          metadata_detectada: null,
          normalizacion_aplicada: false,
          errores: [],
        });
        setQrEditableItems(items);
        setQrMensaje('Lista manual preparada. Revise y cargue cuando esté lista.');
        return;
      }
      const data = await fetchAPI<ListaQRPreview>('/listas/qr/preview', {
        method: 'POST',
        body: JSON.stringify({
          payload: qrPayload,
          modo: qrModo,
          posicion_inicio: Number(qrPosicionInicio) || 0,
          cantidad_bloques: qrCantidadBloques ? Number(qrCantidadBloques) : null,
          total_esperado: qrTotalEsperado ? Number(qrTotalEsperado) : null,
        }),
      });
      setQrPreview(data);
      setQrEditableItems(data.items);
      setQrMensaje('Lista QR procesada. Al cargarla se crea una venta real del local.');
    } catch (err: any) {
      setQrMensaje(err?.message || 'Error procesando lista QR');
    } finally {
      setQrLoading(false);
    }
  };

  const actualizarQrItem = (index: number, patch: Partial<ListaQRItem>) => {
    setQrEditableItems((prev) => prev.map((item, i) => i === index ? { ...item, ...patch } : item));
  };

  const cargarListaExterna = async () => {
    if (!selectedSorteoId || !selectedLocalId || !qrEditableItems.length || qrSaving) {
      setQrMensaje('Seleccione sorteo, vendedor/local y procese la lista antes de cargar.');
      return;
    }
    setQrSaving(true);
    setQrMensaje('');
    try {
      await fetchAPI('/listas/externas', {
        method: 'POST',
        body: JSON.stringify({
          sorteo_id: selectedSorteoId,
          local_id: selectedLocalId,
          vendedor_id: null,
          fecha: fechaFiltro,
          origen: qrOrigen,
          modo: qrModo,
          reventado_destino: selectedSorteo?.tipo_id === 'REVENTADO' ? (qrEditableItems[0]?.tipo_juego || 'REV1') : null,
          payload: qrOrigen === 'QR' ? qrPayload : null,
          posicion_inicio: Number(qrPosicionInicio) || 0,
          cantidad_bloques: qrCantidadBloques ? Number(qrCantidadBloques) : null,
          total_esperado: qrTotalEsperado ? Number(qrTotalEsperado) : null,
          items: qrEditableItems.map((item) => ({
            numero: item.numero,
            tipo_juego: item.tipo_juego,
            monto: Number(item.monto) || 0,
          })),
        }),
      });
      setQrMensaje('Lista externa cargada. Ya se refleja en Listas Consolidadas.');
      setActiveTab('LISTAS');
      setQrPayload('');
      setQrPreview(null);
      setQrEditableItems([]);
      setRefreshListasTick((prev) => prev + 1);
    } catch (err: any) {
      setQrMensaje(err?.message || 'Error cargando lista externa');
    } finally {
      setQrSaving(false);
    }
  };

  const handlePrint = async () => {
    const tipo = selectedSorteo?.tipo_id || 'NORMAL';
    const cols = columnsByTipo(tipo);
    const printRows = formattedRows;
    const localNombre = selectedLocalId
      ? locales.find((l) => l.id === selectedLocalId)?.nombre || selectedLocalId
      : 'Todos los locales';

    const isSunmi = isNativePrintSelected();
    console.log('[PRINT Listas] ─────────────────────────────────');
    console.log('[PRINT Listas] Sorteo:', selectedSorteo?.nombre);
    console.log('[PRINT Listas] isNativePrintSelected:', isSunmi);
    console.log('[PRINT Listas] Filas:', printRows.length);

    // APK nativa: convertir a texto plano para impresora térmica
    if (isSunmi) {
      try {
        console.log('[PRINT Listas] Ruta nativa seleccionada');
        const lines: string[] = [];
        const hr = '================================';
        lines.push('');
        lines.push(hr);
        lines.push(`LISTA ${selectedSorteo?.nombre || ''}`);
        lines.push(`Tipo: ${tipo}`);
        lines.push(hr);
        lines.push(`Fecha: ${fechaFiltro}`);
        lines.push(`Vista: ${localNombre}`);
        lines.push(`Jugadas: ${printRows.length}`);
        lines.push(hr);
        lines.push('');

        // Header row
        if (tipo === 'NORMAL') {
          lines.push('NUM  VEND  LOC   BANCA TOTAL');
          lines.push('--------------------------------');
          for (const row of printRows) {
            const num = String(row.numero ?? '').padEnd(4).slice(0, 4);
            const vend = String(row.monto_vendedor ?? '').padStart(5).slice(-5);
            const loc = String(row.monto_local ?? '').padStart(5).slice(-5);
            const banca = String(row.monto_banca ?? '').padStart(5).slice(-5);
            const total = String(row.monto_total ?? '').padStart(5).slice(-5);
            lines.push(`${num} ${vend} ${loc} ${banca} ${total}`);
          }
        } else {
          // Simplified output for other types
          lines.push(cols.map(c => c.label.slice(0, 6).padEnd(6)).join(' '));
          lines.push('--------------------------------');
          for (const row of printRows) {
            const vals = cols.map(c => String(row[c.key] ?? '').slice(0, 6).padEnd(6));
            lines.push(vals.join(' '));
          }
        }

        // Totals
        if (totalesConsolidado) {
          lines.push('');
          lines.push(hr);
          lines.push(`TOTAL VEND: C${monto(totalesConsolidado.monto_vendedor)}`);
          lines.push(`TOTAL LOC:  C${monto(totalesConsolidado.monto_local)}`);
          lines.push(`TOTAL BANCA:C${monto(totalesConsolidado.monto_banca)}`);
          lines.push(`TOTAL:      C${monto(totalesConsolidado.monto_total)}`);
        }
        lines.push(hr);
        lines.push('\n\n');

        const text = lines.join('\n');
        console.log('[PRINT Listas] [WORKING PATH] Texto:', text.length, 'chars');
        const result = await printRawByDestination(text, 'BANCA LISTAS');
        if (!result.ok) throw new Error(result.error || 'Impresion falló');
        console.log('[PRINT Listas] ✓ impresion exitosa');
        console.log('[PRINT Listas] ─────────────────────────────────');
        return;
      } catch (printErr: any) {
        console.error('[PRINT Listas] ✗ impresion falló:', printErr?.message);
        console.log('[PRINT Listas] Fallback a web...');
        // Fall through to web printing
      }
    }

    // Web: HTML popup
    console.log('[PRINT Listas] Ruta WEB');
    const thead = `<tr>${cols.map((c) => `<th>${c.label}</th>`).join('')}</tr>`;
    const tbody = printRows
      .map((row) => `<tr>${cols.map((c) => `<td>${row[c.key] ?? ''}</td>`).join('')}</tr>`)
      .join('');

    const resumenRows = resumenLocales
      .map((r) => `<tr><td>${r.local_nombre}</td><td>${r.total_jugadas}</td><td>₡${monto(r.total_monto)}</td></tr>`)
      .join('');
    const resumenHtml = resumenLocales.length > 0
      ? `<h3>Resumen por local</h3>
         <table><thead><tr><th>Local</th><th>Jugadas</th><th>Monto</th></tr></thead>
         <tbody>${resumenRows}</tbody>
         <tfoot><tr><td><strong>Total</strong></td><td>${totalJugadas}</td><td>₡${monto(totalMonto)}</td></tr></tfoot></table><br>` : '';

    const totalesHtml = totalesConsolidado
      ? `<tfoot><tr>
          <td><strong>TOTAL</strong></td>
          <td>₡${monto(totalesConsolidado.monto_vendedor)}</td>
          <td>₡${monto(totalesConsolidado.monto_local)}</td>
          <td>₡${monto(totalesConsolidado.monto_banca)}</td>
          <td>₡${monto(totalesConsolidado.monto_total)}</td>
         </tr></tfoot>`
      : '';

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Lista ${selectedSorteo?.nombre || ''} — ${fechaFiltro}</title>
      <style>
        body { font-family: monospace; font-size: 12px; margin: 16px; }
        h2 { margin: 0 0 2px 0; font-size: 16px; }
        h3 { margin: 8px 0 4px 0; font-size: 13px; }
        p { margin: 0 0 8px 0; color: #555; font-size: 11px; }
        table { border-collapse: collapse; width: 100%; margin-bottom: 8px; }
        th { background: #f3f4f6; border: 1px solid #ccc; padding: 4px 8px; text-align: left; font-size: 11px; }
        td { border: 1px solid #ccc; padding: 3px 8px; font-size: 11px; }
        tfoot td { font-weight: bold; background: #f9fafb; }
        @media print { @page { margin: 10mm; } }
      </style></head><body>
      <h2>${selectedSorteo?.nombre || ''} (${tipo})</h2>
      <p>Fecha: ${fechaFiltro} &nbsp;|&nbsp; Vista: ${localNombre} &nbsp;|&nbsp; Jugadas: ${printRows.length}</p>
      ${resumenHtml}
      <h3>Lista consolidada</h3>
      <table><thead>${thead}</thead><tbody>${tbody}</tbody>${totalesHtml}</table>
      <script>window.print(); window.close();</script>
    </body></html>`;

    const win = window.open('', '_blank', 'width=900,height=700');
    if (win) {
      win.document.write(html);
      win.document.close();
      console.log('[PRINT Listas] ✓ Web popup lanzado');
    }
    console.log('[PRINT Listas] ─────────────────────────────────');
  };

  const summaryColumns = [
    { key: 'local_nombre', header: 'Local' },
    { key: 'total_vendedores', header: 'Vendedores', render: (r: ResumenLocalRow) => r.total_vendedores },
    { key: 'total_jugadas', header: 'Jugadas', render: (r: ResumenLocalRow) => r.total_jugadas },
    { key: 'total_monto', header: 'Monto', render: (r: ResumenLocalRow) => `₡${monto(r.total_monto)}` },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Listas Consolidadas</h1>
        <p className="text-gray-500">Consulta general de ventas por sorteo y por local</p>
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('LISTAS')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${activeTab === 'LISTAS' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
        >
          Listas
        </button>
        {esBanca && (
          <button
            onClick={() => setActiveTab('QR')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${activeTab === 'QR' ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
          >
            QR
          </button>
        )}
      </div>

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-sm text-gray-600">Fecha</label>
            <input
              type="date"
              value={fechaFiltro}
              onChange={(e) => setFechaFiltro(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="text-sm text-gray-600">Sorteo</label>
            <select
              value={selectedSorteoId}
              onChange={(e) => setSelectedSorteoId(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
            >
              {sorteos.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre} ({s.tipo_id}){(s.jugadas_hoy ?? 0) > 0 ? ` — ${s.jugadas_hoy} jug.` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-600">Vendedor / Local</label>
            <select
              value={selectedLocalId}
              onChange={(e) => setSelectedLocalId(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">General (todos los locales)</option>
              {locales.map((loc) => (
                <option key={loc.id} value={loc.id}>{loc.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm text-gray-600">Paquete</label>
            <select
              value={selectedPaqueteId}
              onChange={(e) => setSelectedPaqueteId(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">General (sin filtro)</option>
              {paquetes.map((p) => (
                <option key={p.paquete_id} value={p.paquete_id}>
                  {p.paquete_nombre}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {activeTab === 'LISTAS' && esBanca && (
        <Card>
          <div className="space-y-3">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Vista fusionada informativa</h3>
                <p className="text-sm text-amber-700 font-medium">
                  Vista informativa, no modifica datos oficiales.
                </p>
              </div>
              <button
                onClick={() => setFusionActiva((prev) => !prev)}
                className="px-3 py-1.5 text-sm bg-amber-50 hover:bg-amber-100 rounded-lg border border-amber-300 text-amber-900 transition"
              >
                {fusionActiva ? 'Ocultar vista fusionada' : 'Fusionar listas'}
              </button>
            </div>

            {fusionActiva && (
              <div className="space-y-3 border-t border-gray-200 pt-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-sm text-gray-600">Lista A</label>
                    <select
                      value={fusionSorteoAId}
                      onChange={(event) => setFusionSorteoAId(event.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Seleccione Normal/Reventado</option>
                      {sorteosFusionables.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.nombre} ({s.tipo_id})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Lista B</label>
                    <select
                      value={fusionSorteoBId}
                      onChange={(event) => setFusionSorteoBId(event.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Seleccione Normal/Reventado</option>
                      {sorteosFusionables.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.nombre} ({s.tipo_id})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={handleFusionarListas}
                      disabled={!fusionSorteoAId || !fusionSorteoBId || fusionLoading}
                      className="w-full px-3 py-2 text-sm bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-white font-semibold transition"
                    >
                      {fusionLoading ? 'Sumando...' : 'Sumar listas'}
                    </button>
                  </div>
                </div>

                <div className="text-xs text-gray-500">
                  Usa los filtros actuales de fecha, vendedor/local y paquete. Solo permite Normal y Reventado; no incluye Monazos/Monasos ni Parley.
                </div>

                {tipoFusion && fusionFilas.length > 0 && (
                  <div className="flex flex-wrap gap-2 text-sm">
                    <span className="px-2 py-1 rounded bg-gray-100 border border-gray-200 font-semibold text-gray-800">
                      Combinación: {tipoFusion}
                    </span>
                    <span className="px-2 py-1 rounded bg-gray-100 border border-gray-200 text-gray-700">
                      Números con monto: {fusionFilas.length}
                    </span>
                    <span className="px-2 py-1 rounded bg-gray-100 border border-gray-200 text-gray-700">
                      Total temporal: ₡{monto(fusionTotalTemporal)}
                    </span>
                  </div>
                )}

                {fusionMensaje && (
                  <p className="text-sm font-medium text-gray-700">{fusionMensaje}</p>
                )}

                {fusionGridRows.length > 0 && (
                  <div className="space-y-2">
                    <div className="overflow-x-auto">
                      <NumerosGrid
                        rows={fusionGridRows}
                        tipo="REVENTADO"
                        hidePrintButton
                        hideZerosInMontoOrder
                      />
                    </div>
                    <details className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                      <summary className="cursor-pointer font-semibold text-gray-700">
                        Ver detalle R1 / R2
                      </summary>
                      <div className="mt-2 overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="text-left text-gray-600">
                            <tr>
                              <th className="px-2 py-2">Número</th>
                              <th className="px-2 py-2 text-right">R1</th>
                              <th className="px-2 py-2 text-right">R2</th>
                              <th className="px-2 py-2 text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {fusionFilas.map((row) => (
                              <tr key={row.numero}>
                                <td className="px-2 py-2 font-semibold">{row.numero}</td>
                                <td className="px-2 py-2 text-right">₡{monto(row.monto_r1)}</td>
                                <td className="px-2 py-2 text-right">₡{monto(row.monto_r2)}</td>
                                <td className="px-2 py-2 text-right font-semibold">₡{monto(row.monto_total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {activeTab === 'QR' && esBanca && (
        <Card>
          <div className="space-y-4">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2"><QrCode size={18} /> Importar lista QR externa</h3>
              {qrPreview && <span className="text-sm font-semibold text-emerald-700">Total: ₡{monto(qrPreview.total_calculado)}</span>}
            </div>
            <textarea
              value={qrPayload}
              onChange={(event) => setQrPayload(event.target.value)}
              rows={5}
              className="w-full rounded-lg border border-gray-300 px-4 py-3 font-mono text-sm"
              placeholder={qrOrigen === 'QR' ? 'Pegue aquí el texto decodificado del QR' : 'Una línea por número: numero monto tipo_opcional'}
            />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="text-sm text-gray-600">Origen</label>
                <select
                  value={qrOrigen}
                  onChange={(event) => setQrOrigen(event.target.value as 'QR' | 'MANUAL')}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="QR">QR</option>
                  <option value="MANUAL">Manual</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-600">Modo</label>
                <select
                  value={qrModo}
                  onChange={(event) => setQrModo(event.target.value as 'NORMAL_00_99' | 'DIRECTO_REVENTADO')}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="NORMAL_00_99">NORMAL 00-99</option>
                  <option value="DIRECTO_REVENTADO">DIRECTO/REVENTADO</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-600">Posición inicio</label>
                <input type="number" min="0" value={qrPosicionInicio} onChange={(event) => setQrPosicionInicio(event.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="text-sm text-gray-600">Cantidad bloques</label>
                <input type="number" min="1" placeholder={qrModo === 'DIRECTO_REVENTADO' ? '200' : '100'} value={qrCantidadBloques} onChange={(event) => setQrCantidadBloques(event.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="text-sm text-gray-600">Total esperado</label>
                <input type="number" min="0" value={qrTotalEsperado} onChange={(event) => setQrTotalEsperado(event.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
            {selectedSorteo?.tipo_id === 'REVENTADO' && (
              <div className="max-w-xs">
                <label className="text-sm text-gray-600">Cargar reventado en</label>
                <select
                  value={qrEditableItems[0]?.tipo_juego || 'REV1'}
                  onChange={(event) => setQrEditableItems((prev) => prev.map((item) => ({ ...item, tipo_juego: event.target.value })))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="REV1">Num1 / Directo</option>
                  <option value="REV2">Num2 / Reventado</option>
                </select>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={procesarQR}
                disabled={!qrPayload.trim() || qrLoading}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-white transition"
              >
                <QrCode size={15} /> {qrLoading ? 'Procesando...' : 'Procesar'}
              </button>
              <button
                disabled={!qrPreview || qrSaving}
                onClick={cargarListaExterna}
                className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg border border-gray-300 transition"
              >
                {qrSaving ? 'Cargando...' : 'Cargar a lista'}
              </button>
              {qrMensaje && <span className="text-sm font-medium text-gray-700">{qrMensaje}</span>}
            </div>
            {qrPreview && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 text-sm">
                  <div><p className="text-gray-500">Estructura</p><p className="font-semibold text-gray-900">{qrPreview.estructura_detectada}</p></div>
                  <div><p className="text-gray-500">Bloques</p><p className="font-semibold text-gray-900">{qrPreview.cantidad_bloques}</p></div>
                  <div><p className="text-gray-500">Longitud</p><p className="font-semibold text-gray-900">{qrPreview.longitud_normalizada}</p></div>
                  <div><p className="text-gray-500">Total</p><p className="font-semibold text-gray-900">₡{monto(qrPreview.total_calculado)}</p></div>
                  <div><p className="text-gray-500">Fecha QR</p><p className="font-semibold text-gray-900">{qrPreview.metadata_detectada?.fecha_iso || 'Sin detectar'}</p></div>
                </div>
                {qrPreview.normalizacion_aplicada && <p className="text-sm text-blue-700">Se normalizaron bloques desplazados porque el total reportado del QR coincidió con el total corregido.</p>}
                {qrPreview.remanente_hex && <p className="text-sm text-amber-700">Metadata al final: {qrPreview.remanente_hex}</p>}
                <div className="max-h-80 overflow-auto rounded-lg border border-gray-200">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 text-left text-gray-600">
                      <tr>
                        <th className="px-4 py-3">Número</th>
                        <th className="px-4 py-3">Tipo</th>
                        <th className="px-4 py-3">Bloque HEX</th>
                        <th className="px-4 py-3">Interpretado</th>
                        <th className="px-4 py-3 text-right">Monto decimal</th>
                        <th className="px-4 py-3 text-right">Posición</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {qrEditableItems.map((item, index) => (
                        <tr key={`${item.tipo_juego || 'NORMAL'}-${item.numero}-${item.posicion}`}>
                          <td className="px-4 py-3 font-semibold text-gray-900">
                            <input value={item.numero} onChange={(event) => actualizarQrItem(index, { numero: event.target.value })} className="w-16 rounded border border-gray-300 px-2 py-1" />
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            <input value={item.tipo_juego || ''} onChange={(event) => actualizarQrItem(index, { tipo_juego: event.target.value || null })} className="w-28 rounded border border-gray-300 px-2 py-1" placeholder="GENERAL" />
                          </td>
                          <td className="px-4 py-3 font-mono text-gray-700">{item.bloque_hex}</td>
                          <td className="px-4 py-3 font-mono text-gray-700">{item.normalizado ? item.bloque_hex_interpretado : '-'}</td>
                          <td className="px-4 py-3 text-right font-semibold">
                            <input type="number" min="0" value={item.monto} onChange={(event) => actualizarQrItem(index, { monto: Number(event.target.value) || 0 })} className="w-28 rounded border border-gray-300 px-2 py-1 text-right" />
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500">{item.posicion}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {activeTab === 'LISTAS' && <Card>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900">Lista consolidada</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleEnviarWa}
              disabled={rows.length === 0 || enviandoWa}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 hover:bg-green-100 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg border border-green-300 text-green-800 transition"
            >
              {enviandoWa ? 'Enviando…' : 'Enviar por WhatsApp'}
            </button>
            <button
              onClick={handlePrint}
              disabled={formattedRows.length === 0}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg border border-gray-300 transition"
            >
              <Printer size={15} />
              Imprimir
            </button>
          </div>
        </div>
        {loadingRows ? (
          <p className="text-sm text-gray-500">Cargando lista...</p>
        ) : (selectedSorteo?.tipo_id === 'NORMAL' || selectedSorteo?.tipo_id === 'REVENTADO') ? (
          <div className="overflow-x-auto">
            <NumerosGrid
              rows={rows}
              tipo={selectedSorteo?.tipo_id as TipoNumerosGrid}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <DynamicListTable
              columns={columnsByTipo(selectedSorteo?.tipo_id || 'NORMAL')}
              rows={formattedRows}
              onSave={async () => { }}
              idField="id"
            />
            {totalesConsolidado && (
              <table className="w-full text-sm mt-1 border-t-2 border-gray-400">
                <tfoot>
                  <tr className="bg-gray-100 font-bold">
                    <td className="px-2 py-2 w-20">TOTAL</td>
                    <td className="px-2 py-2 text-right">₡{monto(totalesConsolidado.monto_vendedor)}</td>
                    <td className="px-2 py-2 text-right">₡{monto(totalesConsolidado.monto_local)}</td>
                    <td className="px-2 py-2 text-right">₡{monto(totalesConsolidado.monto_banca)}</td>
                    <td className="px-2 py-2 text-right">₡{monto(totalesConsolidado.monto_total)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        )}
      </Card>}

      {activeTab === 'LISTAS' && <Card>
        <h3 className="font-semibold text-gray-900 mb-2">Resumen por local</h3>
        <div className="overflow-x-auto">
          <DataTable
            columns={summaryColumns as any}
            data={resumenLocales}
            keyExtractor={(r: ResumenLocalRow) => r.local_id}
            isLoading={loadingResumen}
            emptyMessage="Sin movimientos para este sorteo"
          />
        </div>
      </Card>}

      {activeTab === 'LISTAS' && <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><p className="text-sm text-gray-500">Locales con ventas</p><p className="text-xl font-bold">{resumenLocales.length}</p></Card>
        <Card><p className="text-sm text-gray-500">Vendedores (suma)</p><p className="text-xl font-bold">{totalVendedores}</p></Card>
        <Card><p className="text-sm text-gray-500">Jugadas activas</p><p className="text-xl font-bold">{totalJugadas}</p></Card>
        <Card><p className="text-sm text-gray-500">Monto vendido</p><p className="text-xl font-bold">₡{monto(totalMonto)}</p></Card>
      </div>}

      {/* Workflow: listas de locales pendientes de oficializar */}
      {activeTab === 'LISTAS' && esBanca && selectedSorteoId && (
        <Card>
          <h3 className="font-semibold text-gray-900 mb-3">
            Listas de Locales — Pendientes de Oficializar
          </h3>

          {msgOficializar && (
            <div className={`mb-3 px-3 py-2 rounded text-sm font-medium ${
              msgOficializar.toLowerCase().includes('error')
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-green-50 text-green-800 border border-green-200'
            }`}>
              {msgOficializar}
            </div>
          )}

          {pendientes.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No hay listas de locales pendientes de oficializar para este sorteo.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 mb-2">
                {pendientes.length} local(es) han enviado listas. Al oficializar se procesan todas juntas.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-3 py-2 text-left font-semibold">Local</th>
                      <th className="border border-gray-200 px-3 py-2 text-right font-semibold">Jugadas</th>
                      <th className="border border-gray-200 px-3 py-2 text-left font-semibold">Enviada</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendientes.map((lp) => (
                      <tr key={lp.id} className="hover:bg-gray-50">
                        <td className="border border-gray-200 px-3 py-2 font-medium">{lp.local_nombre}</td>
                        <td className="border border-gray-200 px-3 py-2 text-right">{lp.jugadas_count}</td>
                        <td className="border border-gray-200 px-3 py-2 text-gray-500 text-xs">
                          {lp.enviada_banca_at ? lp.enviada_banca_at.replace('T', ' ').slice(0, 16) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                onClick={handleOficializar}
                disabled={loadingOficializar}
                className="mt-2 px-5 py-2 text-sm font-semibold bg-green-600 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-green-700 transition"
              >
                {loadingOficializar ? 'Oficializando...' : `Oficializar ${pendientes.length} lista(s)`}
              </button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default ListasPage;
